import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ROUTES } from '@/shared/constants/routes'
import { FEATURE_FLAGS } from '@/shared/constants/feature-flags'
import type { FeatureFlag } from '@/shared/constants/feature-flags'
import {
  useFeatureEnabled,
  useFeatureFilter,
} from '@/shared/hooks/use-feature-enabled'
import { useUser } from '@/shared/hooks/use-user'
import {
  agentsMan,
  easyClaims,
  getPaidBack,
  manageYourPolicy,
} from '@/shared/assets'

export function getLoginRedirectSearch(location: {
  pathname: string
  search?: Record<string, string>
}) {
  const email = (location.search?.email as string) || undefined
  const isAuthOrHome =
    location.pathname === '/' || location.pathname.startsWith('/auth')
  const redirect = !isAuthOrHome ? location.pathname : undefined
  return { email, redirect }
}

export interface HomeQuickAction {
  to: string
  icon: string
  label: string
  testId: string
  flag: FeatureFlag
}

export function useHomePage() {
  const { t, i18n } = useTranslation()
  const { user, isLoading: isUserLoading } = useUser()

  const quickActions = useFeatureFilter<HomeQuickAction>([
    {
      to: ROUTES.CLAIMS,
      icon: easyClaims,
      label: t('home.quickActions.claims'),
      testId: 'home-quick-action-claims',
      flag: FEATURE_FLAGS.WEB_APP_CLAIMS,
    },
    {
      to: ROUTES.DOCUMENTS,
      icon: manageYourPolicy,
      label: t('home.quickActions.documents'),
      testId: 'home-quick-action-documents',
      flag: FEATURE_FLAGS.WEB_APP_DOCUMENTS,
    },
    {
      to: ROUTES.PAYMENT,
      icon: getPaidBack,
      label: t('home.quickActions.payment'),
      testId: 'home-quick-action-payment',
      flag: FEATURE_FLAGS.WEB_APP_PAYMENT,
    },
    {
      to: ROUTES.CONTACT,
      icon: agentsMan,
      label: t('home.quickActions.contact'),
      testId: 'home-quick-action-contact',
      flag: FEATURE_FLAGS.WEB_APP_CONTACT,
    },
  ])

  const showCoverages = !!useFeatureEnabled(FEATURE_FLAGS.WEB_APP_PERKS)
  const coveragesLink = { label: t('home.coverages.seeAll'), to: ROUTES.PERKS }

  const showBilling = !!useFeatureEnabled(FEATURE_FLAGS.WEB_APP_BILLING)
  const billingItems = getDummyBillingItems(
    i18n.language,
    t('home.billing.paid'),
  )

  // TODO: download the invoice once the billing API is available.
  function downloadBill() {
    return undefined
  }

  const isReferEnabled = useFeatureEnabled(FEATURE_FLAGS.WEB_APP_REFER)
  const [isReferModalOpen, setIsReferModalOpen] = useState(false)
  const isCrossSellRentersEnabled = useFeatureEnabled(
    FEATURE_FLAGS.WEB_APP_CROSS_SELL_RENTERS,
  )

  return {
    firstName: user?.givenName ?? user?.name ?? '',
    ownerName: user?.fullName ?? user?.name ?? '',
    isUserLoading,
    quickActions,
    showCoverages,
    coveragesLink,
    showBilling,
    billingItems,
    downloadBill,
    showRefer: !!isReferEnabled,
    isReferModalOpen,
    openReferModal: () => setIsReferModalOpen(true),
    closeReferModal: () => setIsReferModalOpen(false),
    showCrossSellRenters: !!isCrossSellRentersEnabled,
  }
}

export interface HomeBillingItem {
  id: string
  name: string
  period: string
  status: string
  amount: string
}

// TODO: replace with the billing history API once the billing feature lands.
function getDummyBillingItems(
  language: string,
  status: string,
): HomeBillingItem[] {
  const period = new Intl.DateTimeFormat(language, {
    month: 'long',
    year: 'numeric',
  }).format(new Date(2026, 6, 1))
  const amount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(45)
  return Array.from({ length: 4 }, (_, index) => ({
    id: `dummy-billing-${index}`,
    name: 'Oliver',
    period,
    status,
    amount,
  }))
}
